import { Controller } from "web-ui/controller"
import * as Log from "web-ui/log"

const defaultConfirmationText = "Changes you made will not be saved"

const requiredSelector = "[data-leave-confirmation-required]"

// Connects to data-controller="leave-confirmation"
export default class extends Controller {
  static values = {
    message: String
  }

  static events = {
    "click@document": "click:capture",
    "turbo:visit@document": "turboVisit",
    "beforeunload@window": "beforeUnload",
    "turbo:before-stream-render@document": "beforeStreamRender"
  }

  connect() {
    this.debug("Connect", { confirmationText: this.confirmationText })

    this.allowNavigation = false

    // The events table can only reach the window and the document, and the
    // navigate event is dispatched at the Navigation object. A browser that
    // lacks the Navigation API gets no confirmation for a back or forward
    // traversal between entries that Turbo pushed. - Aaron, Mon Sep 28 2026
    this.navigateListener = (event) => this.navigate(event)
    window.navigation?.addEventListener("navigate", this.navigateListener)
  }

  disconnect() {
    this.debug("Disconnect")

    window.navigation?.removeEventListener("navigate", this.navigateListener)
  }

  click(event) {
    const target = event.target

    // A cancel link and an exempted element are departures that the
    // application has already decided need no confirmation. Allowing the
    // departure is what exempts the document unload that follows a click on
    // one of them; returning alone would exempt the click and leave the
    // browser to confirm the unload. - Aaron, Mon Sep 21 2026
    if (target.id === "cancel" || target.dataset.discardChangesConfirmation == "false") {
      this.allowNavigation = true
      return
    }

    if (!this.requiresConfirmation()) {
      return
    }

    const targetLink = event.target.closest("a")

    if (targetLink == null) {
      return
    }

    if (targetLink.target === "_blank") {
      return
    }

    const confirmed = confirm(this.confirmationText)

    if (confirmed) {
      this.allowNavigation = true
    } else {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  // If the page being navigated to includes the meta tag to indicate that turbo
  // should reload the page, rather than proceeding with the turbo navigation,
  // then the page reload will trigger beforeunload.
  // At this point, we no longer need to protect against leaving because either
  // the departure has already been confirmed, or there was nothing to confirm.
  // - Aaron, Mon Sep 21 2026
  turboVisit() {
    this.allowNavigation = true
  }

  // Turbo handles a traversal between the entries that it pushed as a
  // restoration visit, without unloading the document, so beforeunload never
  // runs. Turbo's popstate listener is installed before any controller
  // connects, so it has already started the restoration visit by the time a
  // popstate listener here could run. The navigate event is dispatched before
  // the traversal commits, and cancelling it leaves the history entry and the
  // page in place.
  //
  // The browser withholds cancellation of a traversal unless the user has
  // interacted with the page since the last cancelled one, so a repeated back
  // without an interaction in between leaves without confirmation. Confirming
  // a traversal that cannot be cancelled would offer a choice the browser does
  // not honor. - Aaron, Mon Sep 28 2026
  navigate(event) {
    if (event.navigationType !== "traverse") {
      return
    }

    if (!event.cancelable) {
      return
    }

    if (this.allowNavigation) {
      return
    }

    if (!this.requiresConfirmation()) {
      return
    }

    const confirmed = confirm(this.confirmationText)

    if (confirmed) {
      this.allowNavigation = true
    } else {
      event.preventDefault()
    }
  }

  beforeUnload(event) {
    if (this.allowNavigation) {
      return
    }

    if (!this.requiresConfirmation()) {
      return
    }

    event.preventDefault()

    // Support for older browsers
    event.returnValue = this.confirmationText
    return this.confirmationText
  }

  requiresConfirmation() {
    return this.element.matches(requiredSelector) ||
      this.element.querySelector(requiredSelector) != null
  }

  beforeStreamRender(event) {
    const turboStreamElement = event.detail.newStream
    const action = turboStreamElement.getAttribute("action")

    if (action == "redirect") {
      this.allowNavigation = true
    }
  }

  get confirmationText() {
    return this.messageValue || defaultConfirmationText
  }

  debug(message, data) {
    Log.debug("Leave Confirmation - " + message, data)
  }
}
